/**
 * ShareConfigForm - Configuration form for share settings
 * Allows user to configure visible sections, fields, and expiry
 * Shows real chart values next to checkboxes for preview
 */

import React from 'react';
import {
  VStack,
  Box,
  Heading,
  Checkbox,
  RadioGroup,
  Radio,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  Alert,
  AlertIcon,
  Divider,
} from '@chakra-ui/react';
import { ShareConfig } from '../../types/share.types';

interface ShareConfigFormProps {
  config: ShareConfig;
  onChange: (config: ShareConfig) => void;
  chartData?: any; // Chart API response data for showing real values
}

// Extract field values from chart data
function getFieldValues(chartData: any) {
  const basicInfo = chartData?.basic_info || {};
  return {
    name: basicInfo.chart_name || basicInfo.place_name || '',
    birthTime: `${basicInfo.date || ''} ${basicInfo.time || ''}`.trim(),
    birthPlace: basicInfo.place_name || '',
    coordinates: basicInfo.location || '',
    timezone: basicInfo.timezone || '',
  };
}

export function ShareConfigForm({ config, onChange, chartData }: ShareConfigFormProps) {
  const hiddenFieldCount = Object.values(config.fields).filter(v => !v).length;
  const fieldValues = chartData ? getFieldValues(chartData) : null;

  return (
    <VStack spacing={6} align="stretch">
      {/* Visible Fields */}
      <Box>
        <Heading size="sm" mb={3}>Visible Fields</Heading>
        <VStack align="start" spacing={2} pl={2}>
          <HStack w="100%" justify="space-between">
            <Checkbox
              isChecked={config.fields.name}
              onChange={(e) => onChange({
                ...config,
                fields: {
                  ...config.fields,
                  name: e.target.checked
                }
              })}
            >
              Name
            </Checkbox>
            {fieldValues?.name && (
              <Text
                fontSize="xs"
                color="gray.500"
                opacity={config.fields.name ? 1 : 0.4}
                noOfLines={1}
                maxW="200px"
                textAlign="right"
              >
                {fieldValues.name}
              </Text>
            )}
          </HStack>
          <HStack w="100%" justify="space-between">
            <Checkbox
              isChecked={config.fields.birthTime}
              onChange={(e) => onChange({
                ...config,
                fields: {
                  ...config.fields,
                  birthTime: e.target.checked
                }
              })}
            >
              Birth Time
            </Checkbox>
            {fieldValues?.birthTime && (
              <Text
                fontSize="xs"
                color="gray.500"
                opacity={config.fields.birthTime ? 1 : 0.4}
                noOfLines={1}
                maxW="200px"
                textAlign="right"
              >
                {fieldValues.birthTime}
              </Text>
            )}
          </HStack>
          <HStack w="100%" justify="space-between">
            <Checkbox
              isChecked={config.fields.birthPlace}
              onChange={(e) => onChange({
                ...config,
                fields: {
                  ...config.fields,
                  birthPlace: e.target.checked
                }
              })}
            >
              Birth Place
            </Checkbox>
            {fieldValues?.birthPlace && (
              <Text
                fontSize="xs"
                color="gray.500"
                opacity={config.fields.birthPlace ? 1 : 0.4}
                noOfLines={1}
                maxW="200px"
                textAlign="right"
              >
                {fieldValues.birthPlace}
              </Text>
            )}
          </HStack>
          <HStack w="100%" justify="space-between">
            <Checkbox
              isChecked={config.fields.coordinates}
              onChange={(e) => onChange({
                ...config,
                fields: {
                  ...config.fields,
                  coordinates: e.target.checked
                }
              })}
            >
              Coordinates
            </Checkbox>
            {fieldValues?.coordinates && (
              <Text
                fontSize="xs"
                color="gray.500"
                opacity={config.fields.coordinates ? 1 : 0.4}
                noOfLines={1}
                maxW="200px"
                textAlign="right"
              >
                {fieldValues.coordinates}
              </Text>
            )}
          </HStack>
          <HStack w="100%" justify="space-between">
            <Checkbox
              isChecked={config.fields.timezone}
              onChange={(e) => onChange({
                ...config,
                fields: {
                  ...config.fields,
                  timezone: e.target.checked
                }
              })}
            >
              Timezone
            </Checkbox>
            {fieldValues?.timezone && (
              <Text
                fontSize="xs"
                color="gray.500"
                opacity={config.fields.timezone ? 1 : 0.4}
                noOfLines={1}
                maxW="200px"
                textAlign="right"
              >
                {fieldValues.timezone}
              </Text>
            )}
          </HStack>
        </VStack>
      </Box>

      <Divider />

      {/* Expiry */}
      <Box>
        <Heading size="sm" mb={3}>⏰ Link Expiry</Heading>
        <RadioGroup
          value={config.expires_in_days === null ? 'never' : 'custom'}
          onChange={(value) => {
            if (value === 'never') {
              onChange({ ...config, expires_in_days: null });
            } else {
              onChange({ ...config, expires_in_days: 30 });
            }
          }}
        >
          <VStack align="start" spacing={3} pl={2}>
            <Radio value="never">Never expires</Radio>
            <HStack>
              <Radio value="custom">Expires in</Radio>
              <NumberInput
                value={config.expires_in_days || 30}
                min={1}
                max={365}
                onChange={(_, valueAsNumber) => onChange({
                  ...config,
                  expires_in_days: valueAsNumber
                })}
                isDisabled={config.expires_in_days === null}
                size="sm"
                w="100px"
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm">days</Text>
            </HStack>
          </VStack>
        </RadioGroup>
      </Box>

      <Divider />

      {/* Preview Summary */}
      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <VStack align="start" spacing={1} flex={1}>
          <Text fontSize="sm" fontWeight="medium">
            Summary:
          </Text>
          <Text fontSize="xs">
            • {hiddenFieldCount} field{hiddenFieldCount !== 1 ? 's' : ''} will be hidden
          </Text>
          <Text fontSize="xs">
            • Link {config.expires_in_days ? `expires in ${config.expires_in_days} days` : 'never expires'}
          </Text>
        </VStack>
      </Alert>
    </VStack>
  );
}
